import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Heart, CheckCircle, Users, Clock, Shield, Stethoscope } from 'lucide-react';

const Nurses = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What should nurses include in their performance review?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Include patient satisfaction scores, successful interventions, certifications earned, infection prevention metrics, patient education initiatives, and mentorship activities. Quantify where possible: 'Maintained 98% patient satisfaction rating across 450+ patients this year.'"
        }
      },
      {
        "@type": "Question",
        "name": "How do nurses demonstrate leadership in performance reviews?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Document charge nurse shifts, precepting new nurses, committee participation, protocol improvements you've suggested, and crisis management situations. Example: 'Precepted 4 new graduate nurses to independent practice, all passing competency evaluations on first attempt.'"
        }
      },
      {
        "@type": "Question",
        "name": "What metrics matter for nursing performance reviews?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Key metrics include: HCAHPS scores, fall prevention rates, medication error rates (or lack thereof), patient throughput, documentation compliance, and readmission prevention. Also track certifications, continuing education hours, and quality improvement participation."
        }
      },
      {
        "@type": "Question",
        "name": "How can nurses track achievements during busy shifts?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Use a mobile-friendly tool like BragBox to quickly log wins between patients. Even 30 seconds after a successful intervention or positive patient feedback can capture an achievement you'd otherwise forget. Set a weekly reminder to review and add any missed wins."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Performance Review Tips for Nurses | BragBox</title>
        <meta name="description" content="Ace your nursing performance review with actionable tips. Learn how to document patient care achievements, quantify clinical impact, and showcase your contributions to patient outcomes." />
        <meta name="keywords" content="nurse performance review, nursing self-assessment, healthcare achievements, clinical metrics, nursing career growth, HCAHPS scores" />
        <link rel="canonical" href="https://bragbox.ai/tips/nurses" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#1F2A44] via-[#2d3a5c] to-[#1F2A44]">
        {/* Header */}
        <header className="bg-[#1F2A44]/90 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                <span className="text-[#1F2A44] font-bold text-sm">B</span>
              </div>
              <span className="text-white font-semibold text-lg">BragBox</span>
            </Link>
            <Link
              to="/login?signup=true"
              className="bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Start free
            </Link>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#C4A35A]/20 text-[#C4A35A] px-4 py-2 rounded-full text-sm mb-6">
              <Heart className="w-4 h-4" />
              Healthcare Career Tips
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Performance Review Tips for Nurses
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Your 12-hour shifts are packed with critical decisions, patient care, and life-saving moments. Learn how to document the impact that often goes unnoticed and get the recognition you deserve.
            </p>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-12 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Why Nurses Struggle with Self-Evaluations</h2>
            <p className="text-gray-300 mb-6">
              You caught the subtle change in a patient's condition that prevented a code. You calmed an anxious family while managing three other critical patients. You trained a new grad while handling a full patient load. But when it's time for your annual review, where do you even start?
            </p>
            <p className="text-gray-300">
              Nursing work is often invisible precisely because you're good at it. When you prevent problems, there's no incident report. When you educate patients perfectly, they don't come back. Your success can look like nothing happening at all.
            </p>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">10 Actionable Tips for Your Nursing Review</h2>
            
            <div className="space-y-8">
              {/* Tip 1 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Stethoscope className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">1. Track Patient Satisfaction Metrics</h3>
                    <p className="text-gray-300 mb-3">
                      HCAHPS scores and patient feedback are concrete evidence of care quality. Request your individual scores if available, and document specific compliments from patients or families.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Maintained 96% patient satisfaction rating across 380+ patients. Received 12 written commendations from patients and families specifically mentioning communication and compassion."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 2 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">2. Document Safety and Quality Improvements</h3>
                    <p className="text-gray-300 mb-3">
                      Zero falls, zero infections, zero medication errors aren't nothing. They're evidence of vigilance. Track your unit's metrics and your contribution to them.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Zero patient falls across 1,200+ patient-hours of care. Identified and implemented bedside rounding change that reduced unit fall rate by 30% over 6 months."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 3 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">3. Highlight Early Deterioration Recognition</h3>
                    <p className="text-gray-300 mb-3">
                      Those moments when your clinical instinct caught something before it became critical? Those are worth documenting. They show clinical expertise that saves lives.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Recognized early sepsis signs in post-surgical patient based on subtle vital sign trends, escalating care 4 hours before critical deterioration. Patient transferred to ICU, stabilized, and discharged home 5 days later."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 4 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">4. Track Mentorship and Precepting</h3>
                    <p className="text-gray-300 mb-3">
                      Precepting new nurses while managing your own patients is leadership in action. Document how many nurses you've trained and their outcomes.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Precepted 3 new graduate nurses through 12-week orientation, all achieving independent practice status. One mentee received unit's New Nurse of the Quarter award."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 5 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">5. Document Certifications and Education</h3>
                    <p className="text-gray-300 mb-3">
                      Specialty certifications, CEU courses, and skills validations show commitment to excellence. Don't assume your manager remembers what you've earned.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Achieved CCRN certification (first attempt pass). Completed 45 CEU hours including advanced cardiac life support recertification and trauma nursing core course."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 6 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">6</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">6. Capture Patient Education Successes</h3>
                    <p className="text-gray-300 mb-3">
                      Effective patient education reduces readmissions and improves outcomes. Track patients you've educated on disease management, medications, or self-care.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Developed and implemented diabetic foot care education protocol. 30-day readmission rate for diabetic patients decreased by 22% in months following implementation."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 7 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">7</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">7. Note Charge Nurse and Leadership Shifts</h3>
                    <p className="text-gray-300 mb-3">
                      Every charge shift is leadership experience. Track how many you've worked, staffing challenges you managed, and crises you navigated.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Served as charge nurse for 48 shifts, managing staffing for 6-nurse unit. Successfully coordinated care during 3 code blues and 2 rapid responses with zero adverse outcomes."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 8 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">8</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">8. Document Committee and Council Participation</h3>
                    <p className="text-gray-300 mb-3">
                      Quality councils, shared governance, and unit committees show engagement beyond bedside care. Track your contributions and their impact.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Active member of unit practice council. Led initiative to standardize handoff communication, resulting in 40% reduction in information gaps identified during shift change audits."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 9 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">9</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">9. Highlight Interdisciplinary Collaboration</h3>
                    <p className="text-gray-300 mb-3">
                      Effective communication with physicians, case managers, therapists, and social workers drives patient outcomes. Document moments when your collaboration made a difference.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Coordinated discharge planning for complex patient requiring home health, DME, and outpatient follow-up across 4 specialties. Patient safely discharged 2 days ahead of expected LOS, avoiding $4,200 in additional hospital costs."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 10 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">10</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">10. Use a Brag Doc Tool That Works on the Floor</h3>
                    <p className="text-gray-300 mb-3">
                      You can't document achievements if the tool isn't accessible between patients. Use something mobile-friendly that lets you log wins in 30 seconds.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Pro Tip:</p>
                      <p className="text-gray-300 text-sm">After a great patient interaction, family thank-you, or clinical catch, take 30 seconds to log it in BragBox. At shift end, review and add anything you missed. Your future self will thank you at review time.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Metrics Examples Section */}
        <section className="py-16 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Key Metrics Nurses Should Track</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Patient Safety</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Falls prevented / fall rate</li>
                  <li>• Medication error prevention</li>
                  <li>• Infection rates (CAUTI, CLABSI)</li>
                  <li>• Pressure injury prevention</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Patient Experience</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• HCAHPS scores (if available)</li>
                  <li>• Patient compliments received</li>
                  <li>• Family satisfaction feedback</li>
                  <li>• Pain management effectiveness</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Professional Growth</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Certifications achieved</li>
                  <li>• CEU hours completed</li>
                  <li>• Skills validations passed</li>
                  <li>• Committees participated in</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Leadership</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Charge shifts worked</li>
                  <li>• Nurses precepted</li>
                  <li>• Process improvements led</li>
                  <li>• Unit goals contributed to</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What should nurses include in their performance review?</h3>
                <p className="text-gray-300">Include patient satisfaction scores, successful interventions, certifications earned, infection prevention metrics, patient education initiatives, and mentorship activities. Quantify where possible: "Maintained 98% patient satisfaction rating across 450+ patients this year."</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How do nurses demonstrate leadership in performance reviews?</h3>
                <p className="text-gray-300">Document charge nurse shifts, precepting new nurses, committee participation, protocol improvements you've suggested, and crisis management situations. Example: "Precepted 4 new graduate nurses to independent practice, all passing competency evaluations on first attempt."</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What metrics matter for nursing performance reviews?</h3>
                <p className="text-gray-300">Key metrics include: HCAHPS scores, fall prevention rates, medication error rates (or lack thereof), patient throughput, documentation compliance, and readmission prevention. Also track certifications, continuing education hours, and quality improvement participation.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How can nurses track achievements during busy shifts?</h3>
                <p className="text-gray-300">Use a mobile-friendly tool like BragBox to quickly log wins between patients. Even 30 seconds after a successful intervention or positive patient feedback can capture an achievement you'd otherwise forget. Set a weekly reminder to review and add any missed wins.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-[#C4A35A]/20 to-[#C4A35A]/10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Your Care Deserves Recognition</h2>
            <p className="text-xl text-gray-300 mb-8">
              BragBox helps nurses track achievements on the go, so you're always ready to showcase your impact at review time.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/login?signup=true"
                className="inline-block bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              >
                Start free
              </Link>
              <Link to="/login" className="text-gray-300 underline underline-offset-4 hover:text-white transition-colors">
                Already have an account? Log in.
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                  <span className="text-[#1F2A44] font-bold text-sm">B</span>
                </div>
                <span className="text-white font-semibold">BragBox</span>
              </Link>
              <div className="flex gap-6 text-gray-400 text-sm">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                <Link to="/login" className="hover:text-white transition-colors">Login</Link>
              </div>
              <p className="text-gray-500 text-sm">© 2026 BragBox. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Nurses;
